import React, { useEffect, useState } from "react";
import { FileDown, MessageCircle, X, Save } from "lucide-react";
import { toast } from "sonner";
import { apiGetMarkup, apiSaveQuote } from "@/api";
import { useTenant } from "@/context/TenantContext";
import { formatINR } from "@/mock";
import { buildQuotationPdf, quoteTotals } from "@/lib/quotationPdf";

export const MAX_QUOTE = 5;

const LEG_KEYS = ["code", "airline", "flightNo", "depTime", "arrTime", "duration", "stops", "via", "from", "to", "date", "price"];
const slim = (items) => items.map((it) => ({ ...it, legs: it.legs.map((f) => Object.fromEntries(LEG_KEYS.map((k) => [k, f[k]]))) }));

export const makeQuotePdf = (brand, search, items, mk, extra = {}) => buildQuotationPdf({
  ...search, items, markup: mk, contact: brand.contact, ...extra,
  brand: { name: brand.name || "FLYTRIP", primary: brand.tenant?.primary_color || "#1B1F5B", secondary: brand.tenant?.secondary_color || "#F5B32B" },
});

export const shareQuote = async (brand, search, items, mk, extra = {}) => {
  const { doc, fileName } = await makeQuotePdf(brand, search, items, mk, extra);
  const file = new File([doc.output("blob")], fileName, { type: "application/pdf" });
  if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: "Flight Quotation" }); return; }
  doc.save(fileName);
  const leg = (f) => `${f.airline} ${f.flightNo || ""} ${f.from}-${f.to} ${f.depTime}-${f.arrTime} (${f.stops === 0 ? "Non-stop" : `${f.stops} stop`})`;
  const lines = items.map((it, i) => `${i + 1}. ${it.legs.map(leg).join("\n   + ")}\n   Total: ${formatINR(quoteTotals(it, search.pax, mk).total)}`);
  const when = search.retDate ? `${search.date} to ${search.retDate}` : search.date;
  const hi = extra.clientName ? `Dear ${extra.clientName},\n` : "";
  const text = `${hi}Flight Quotation: ${search.fromCity} (${search.from}) to ${search.toCity} (${search.to}), ${when}, ${search.pax} pax\n\n${lines.join("\n\n")}\n\n${brand.name || "FLYTRIP"}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
};

export const QuoteCheck = ({ checked, onToggle, id }) => (
  <label data-testid={`quote-check-${id}`} className="flex cursor-pointer items-center gap-1.5 text-[11px] font-bold text-[#1B1F5B]">
    <input type="checkbox" checked={checked} onChange={onToggle} className="h-4 w-4 accent-[#1B1F5B]" />
    Add to quote
  </label>
);

const MarkupEdit = ({ mk, setMk }) => (
  <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1" data-testid="quote-markup-edit">
    <span className="text-[11px] font-bold text-slate-500">Markup</span>
    <input type="number" min="0" data-testid="quote-markup-input" value={mk.flight ?? 0}
      onChange={(e) => setMk({ ...mk, flight: Math.max(0, parseFloat(e.target.value) || 0) })}
      className="w-16 rounded border border-slate-200 px-1.5 py-1 text-xs font-bold outline-none focus:border-[#1B1F5B]" />
    <select data-testid="quote-markup-type" value={mk.flightType || "flat"} onChange={(e) => setMk({ ...mk, flightType: e.target.value })}
      className="rounded border border-slate-200 px-1 py-1 text-xs font-bold outline-none">
      <option value="flat">₹</option>
      <option value="percent">%</option>
    </select>
  </div>
);

const btn = "flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-extrabold disabled:opacity-60";

export const QuoteBar = ({ items, search, onClear, raised = false }) => {
  const brand = useTenant();
  const [mk, setMk] = useState({ flight: 0, flightType: "flat" });
  const [client, setClient] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiGetMarkup().then((m) => setMk({ flight: m.flight || 0, flightType: m.flightType || "flat" })).catch(() => {}); }, []);
  if (!items.length) return null;

  const extra = { clientName: client.trim() };
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { if (e?.name !== "AbortError") toast.error("Something went wrong, please try again"); } finally { setBusy(false); }
  };
  const download = () => run(async () => { const { doc, fileName } = await makeQuotePdf(brand, search, items, mk, extra); doc.save(fileName); toast.success("Quotation downloaded"); });
  const share = () => run(() => shareQuote(brand, search, items, mk, extra));
  const save = () => run(async () => {
    await apiSaveQuote({ client_name: extra.clientName, search, items: slim(items), markup: mk, totals: items.map((it) => quoteTotals(it, search.pax, mk).total) });
    toast.success("Quotation saved in My Quotations");
  });

  return (
    <div data-testid="quote-bar" className={`fixed inset-x-0 z-[45] border-t border-slate-200 bg-white/95 px-4 py-2.5 shadow-[0_-10px_30px_rgba(27,31,91,0.12)] backdrop-blur ${raised ? "bottom-[132px] sm:bottom-[74px]" : "bottom-0"}`}>
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2">
        <p className="text-sm font-bold text-slate-800" data-testid="quote-count">{items.length}/{MAX_QUOTE} in quotation</p>
        <input data-testid="quote-client-input" value={client} onChange={(e) => setClient(e.target.value)} maxLength={80} placeholder="Client name"
          className="w-32 rounded-lg border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-[#1B1F5B]" />
        <MarkupEdit mk={mk} setMk={setMk} />
        <div className="ml-auto flex flex-wrap gap-2">
          <button data-testid="quote-clear-btn" onClick={onClear} className={`${btn} border border-slate-200 font-bold text-slate-600 hover:bg-slate-50`}><X className="h-3.5 w-3.5" />Clear</button>
          <button data-testid="quote-save-btn" disabled={busy} onClick={save} className={`${btn} border-2 border-[#1B1F5B] text-[#1B1F5B] hover:bg-[#1B1F5B]/5`}><Save className="h-4 w-4" />Save quote</button>
          <button data-testid="quote-whatsapp-btn" disabled={busy} onClick={share} className={`${btn} bg-[#25D366] text-white hover:brightness-95`}><MessageCircle className="h-4 w-4" />WhatsApp</button>
          <button data-testid="quote-download-btn" disabled={busy} onClick={download} className={`${btn} bg-[#1B1F5B] px-4 text-white hover:brightness-110`}><FileDown className="h-4 w-4" />{busy ? "Preparing..." : "Download PDF"}</button>
        </div>
      </div>
    </div>
  );
};
